/**
 * EcctrlPlayer - Local player controller built on ecctrl v2
 *
 * ecctrl v2 is a rewrite of v1: it no longer reads drei KeyboardControls itself,
 * no longer ships a follow camera, and renamed most tuning props. This file
 * wires v2 correctly:
 *   - keyboard + joystick + virtual buttons -> ecctrlRef.setMovement() each frame
 *   - a minimal third-person follow camera (ecctrl v2 has none built in)
 *   - existing Midgard interaction detection + multiplayer position sync
 */

import { useRef, useEffect, useCallback, useState, useMemo, forwardRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { KeyboardControls, useKeyboardControls } from '@react-three/drei';
import { Ecctrl } from 'ecctrl';
import { useJoystickStore, useButtonStore } from 'ecctrl/input';
import * as THREE from 'three';
import HumanoidCharacter from './character/HumanoidCharacter';

// Keyboard control mapping (consumed by drei KeyboardControls, forwarded to ecctrl)
export const KEYBOARD_MAP = [
  { name: 'forward', keys: ['KeyW', 'ArrowUp'] },
  { name: 'backward', keys: ['KeyS', 'ArrowDown'] },
  { name: 'leftward', keys: ['KeyA', 'ArrowLeft'] },
  { name: 'rightward', keys: ['KeyD', 'ArrowRight'] },
  { name: 'jump', keys: ['Space'] },
  { name: 'run', keys: ['ShiftLeft', 'ShiftRight'] },
  { name: 'action1', keys: ['KeyE'] },
  { name: 'action2', keys: ['KeyF'] },
  { name: 'action3', keys: ['KeyQ'] },
  { name: 'action4', keys: ['KeyR'] },
];

// ecctrl v2 prop names
const MOVEMENT_CONFIG = {
  maxWalkVel: 3,
  maxRunVel: 6,
  jumpVel: 6,
  enableToggleRun: false, // hold shift / sprint button to run
  floatHeight: 0.2,
  followPlatform: true,

  // Movement direction comes from our own camera yaw (see setForwardDir below),
  // not from ecctrl sampling the camera a frame late.
  useCustomForward: true,

  // Apply the move impulse at the centre of mass. The default (0.5) applies it
  // half a capsule above the centre, which torques the body forward and is what
  // made the character lean while walking.
  moveImpulsePointOffset: 0,

  // Pitch/roll are hard-locked on the rigid body instead (enabledRotations below),
  // so the balance spring has nothing left to correct.
  autoBalance: false,

  // Yaw turn toward the movement direction (unaffected by autoBalance).
  autoBalanceSpringOnY: 0.12,
  autoBalanceDampingOnY: 0.008,
};

// Lock pitch and roll, leave yaw free: no leaning or tipping, normal turning.
const ENABLED_ROTATIONS = [false, true, false];

const CAPSULE_HALF_HEIGHT = 0.5;
const CAPSULE_RADIUS = 0.4;

// Virtual button ids used by NorseMobileControls
const BTN_JUMP = 'jump';
const BTN_RUN = 'run';
const BTN_ACTION = 'action';

const HumanoidCharacterWrapper = forwardRef(function HumanoidCharacterWrapper(props, ref) {
  const { isMoving = false, isRunning = false, isSeated = false, movementDirection } = props;
  return (
    <group ref={ref}>
      <HumanoidCharacter
        position={[0, -0.9, 0]}
        isMoving={isMoving}
        isRunning={isRunning}
        isSeated={isSeated}
        movementDirection={movementDirection}
      />
    </group>
  );
});

/**
 * ThirdPersonCamera - ecctrl v2 removed the built-in follow camera.
 * Orbit with pointer drag on the canvas, zoom with the wheel.
 */
function useThirdPersonCamera({ handleRef, distance, enabled }) {
  const { camera, gl } = useThree();
  const yaw = useRef(Math.PI);
  const pitch = useRef(0.35);
  const dist = useRef(distance);
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const target = useMemo(() => new THREE.Vector3(), []);
  const desired = useMemo(() => new THREE.Vector3(), []);

  useEffect(() => { dist.current = distance; }, [distance]);

  useEffect(() => {
    const el = gl.domElement;
    const down = (e) => {
      if (!enabled.current) return;
      dragging.current = true;
      last.current = { x: e.clientX, y: e.clientY };
      el.setPointerCapture?.(e.pointerId);
    };
    const move = (e) => {
      if (!dragging.current || !enabled.current) return;
      yaw.current -= (e.clientX - last.current.x) * 0.005;
      pitch.current = THREE.MathUtils.clamp(pitch.current + (e.clientY - last.current.y) * 0.004, -0.2, 1.2);
      last.current = { x: e.clientX, y: e.clientY };
    };
    const up = (e) => {
      dragging.current = false;
      el.releasePointerCapture?.(e.pointerId);
    };
    const wheel = (e) => {
      if (!enabled.current) return;
      dist.current = THREE.MathUtils.clamp(dist.current + e.deltaY * 0.01, 2, 18);
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('wheel', wheel, { passive: true });
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('wheel', wheel);
    };
  }, [gl, enabled]);

  return {
    yawRef: yaw,
    update: useCallback((delta) => {
      const h = handleRef.current;
      if (!h || !h.body) return;
      const p = h.body.translation();
      target.set(p.x, p.y + 1.2, p.z);

      const cp = Math.cos(pitch.current);
      desired.set(
        target.x + Math.sin(yaw.current) * cp * dist.current,
        target.y + Math.sin(pitch.current) * dist.current + 0.5,
        target.z + Math.cos(yaw.current) * cp * dist.current
      );
      camera.position.lerp(desired, Math.min(1, delta * 8));
      camera.lookAt(target);
    }, [camera, handleRef, target, desired]),
  };
}

export default function EcctrlPlayer({
  initialPosition = { x: 0, y: 1, z: 10 },
  sceneId,
  onInteractionChange,
  onTriggerInteraction,
  onPositionUpdate,
  onNearbyPlayersChange,
  isSeated = false,
  seatPosition = null,
  seatRotation = 0,
  remotePlayers = [],
  gameSettings = {},
  interactions = [],
  menuOpen = false,
  isChatFocused = false,
}) {
  const characterRef = useRef();
  const ecctrlRef = useRef();
  const [, getKeys] = useKeyboardControls();

  const blocked = menuOpen || isChatFocused || isSeated;
  const enabledRef = useRef(!blocked);
  useEffect(() => { enabledRef.current = !blocked; }, [blocked]);

  const { update: updateCamera, yawRef } = useThirdPersonCamera({
    handleRef: ecctrlRef,
    distance: gameSettings.cameraDistance || 8,
    enabled: enabledRef,
  });

  const currentInteraction = useRef(null);
  const wasActionDown = useRef(false);

  const [animState, setAnimState] = useState({
    isMoving: false,
    isRunning: false,
    movementDirection: { x: 0, z: 0 },
  });

  const lastPositionUpdate = useRef(0);
  const lastAnimUpdate = useRef(0);

  const INTERACTION_PRIORITY = useMemo(() => ({
    talk: 100, stand: 90, sit: 80, door: 70, interact: 50,
  }), []);

  const TALK_RADIUS = 3.5;
  const HYSTERESIS_MULTIPLIER = 1.3;

  const getCurrentRadius = useCallback((baseRadius, interactionId) => (
    currentInteraction.current?.id === interactionId ? baseRadius * HYSTERESIS_MULTIPLIER : baseRadius
  ), []);

  const triggerInteract = useCallback(() => {
    if (currentInteraction.current && onTriggerInteraction) {
      onTriggerInteraction(currentInteraction.current);
    }
  }, [onTriggerInteraction]);

  // Input runs at priority -1 so it lands BEFORE ecctrl's own frame callback.
  // Ecctrl mounts as a child, so its useFrame subscribes first; at equal priority
  // our input would always be one frame stale, which is what made the movement
  // vector feel slightly off from the camera.
  const forwardDir = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    const handle = ecctrlRef.current;
    if (!handle || !handle.body) return;

    // Derive the move basis from the camera yaw we own, not from the lerped
    // camera transform — input then matches the camera exactly, with no lag.
    const yaw = yawRef.current;
    forwardDir.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    handle.setForwardDir(forwardDir);

    const keys = blocked ? {} : getKeys();
    const stick = useJoystickStore.getState().joysticks.default || { x: 0, y: 0 };
    const buttons = useButtonStore.getState().buttons;

    handle.setMovement({
      forward: !!keys.forward,
      backward: !!keys.backward,
      leftward: !!keys.leftward,
      rightward: !!keys.rightward,
      joystick: blocked ? { x: 0, y: 0 } : { x: stick.x || 0, y: stick.y || 0 },
      run: !blocked && (!!keys.run || !!buttons[BTN_RUN]),
      jump: !blocked && (!!keys.jump || !!buttons[BTN_JUMP]),
    });

    // Interact on rising edge of E / action button
    const actionDown = !blocked && (!!keys.action1 || !!buttons[BTN_ACTION]);
    if (actionDown && !wasActionDown.current) triggerInteract();
    wasActionDown.current = actionDown;
  }, -1);

  useFrame((state, delta) => {
    const handle = ecctrlRef.current;
    if (!handle || !handle.body) return;

    updateCamera(delta);

    // ---- state readout ----
    const pos = handle.body.translation();
    const vel = handle.body.linvel();
    const speed = Math.sqrt(vel.x * vel.x + vel.z * vel.z);
    const isMoving = speed > 0.5;
    const isRunning = speed > MOVEMENT_CONFIG.maxWalkVel + 0.5;

    const now = Date.now();
    if (now - lastAnimUpdate.current > 50) {
      lastAnimUpdate.current = now;
      setAnimState(prev => (
        prev.isMoving !== isMoving || prev.isRunning !== isRunning
          ? { isMoving, isRunning, movementDirection: isMoving ? { x: vel.x, z: vel.z } : prev.movementDirection }
          : prev
      ));
    }

    // ---- interaction detection ----
    let bestInteraction = null;
    let bestPriority = -1;
    let bestDistance = Infinity;
    const nearbyPlayerIds = [];

    for (const player of remotePlayers) {
      if (!player.currentPosition) continue;
      const dx = pos.x - player.currentPosition.x;
      const dz = pos.z - player.currentPosition.z;
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance < TALK_RADIUS * 2) nearbyPlayerIds.push(player.playerId);

      const talkId = `talk-${player.playerId}`;
      if (distance < getCurrentRadius(TALK_RADIUS, talkId)) {
        const priority = INTERACTION_PRIORITY.talk;
        if (priority > bestPriority || (priority === bestPriority && distance < bestDistance)) {
          bestPriority = priority;
          bestDistance = distance;
          bestInteraction = {
            id: talkId,
            type: 'talk',
            priority,
            prompt: `Talk to ${player.displayName || 'Player'}`,
            targetPlayer: {
              playerId: player.playerId,
              displayName: player.displayName || 'Player',
              username: player.username || '',
            },
            action: { type: 'talkToPlayer', playerId: player.playerId },
          };
        }
      }
    }

    for (const interaction of interactions) {
      const dx = pos.x - interaction.position.x;
      const dz = pos.z - interaction.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz);
      if (distance < getCurrentRadius(interaction.radius, interaction.id)) {
        const priority = interaction.priority || INTERACTION_PRIORITY.interact;
        if (priority > bestPriority || (priority === bestPriority && distance < bestDistance)) {
          bestPriority = priority;
          bestDistance = distance;
          bestInteraction = interaction;
        }
      }
    }

    if (bestInteraction?.id !== currentInteraction.current?.id) {
      currentInteraction.current = bestInteraction;
      onInteractionChange?.(bestInteraction);
    }

    // ---- multiplayer sync (10fps) ----
    if (now - lastPositionUpdate.current > 100) {
      lastPositionUpdate.current = now;
      const movementState = !isMoving ? 'idle' : (isRunning ? 'running' : 'walking');
      const q = handle.body.rotation();
      const playerRotation = new THREE.Euler().setFromQuaternion(
        new THREE.Quaternion(q.x, q.y, q.z, q.w), 'YXZ'
      ).y;

      onPositionUpdate?.({ x: pos.x, y: pos.y, z: pos.z }, playerRotation, movementState);
      onNearbyPlayersChange?.(nearbyPlayerIds);
    }
  });

  if (isSeated && seatPosition) {
    return (
      <group position={[seatPosition.x, seatPosition.y, seatPosition.z]} rotation={[0, seatRotation, 0]}>
        <HumanoidCharacter position={[0, 0, 0]} isMoving={false} isRunning={false} isSeated={true} />
      </group>
    );
  }

  return (
    <Ecctrl
      ref={ecctrlRef}
      position={[initialPosition.x, initialPosition.y, initialPosition.z]}
      capsuleHalfHeight={CAPSULE_HALF_HEIGHT}
      capsuleRadius={CAPSULE_RADIUS}
      enable={!blocked}
      enabledRotations={ENABLED_ROTATIONS}
      {...MOVEMENT_CONFIG}
    >
      <HumanoidCharacterWrapper
        ref={characterRef}
        isMoving={animState.isMoving}
        isRunning={animState.isRunning}
        movementDirection={animState.movementDirection}
      />
    </Ecctrl>
  );
}

export function EcctrlKeyboardWrapper({ children }) {
  return <KeyboardControls map={KEYBOARD_MAP}>{children}</KeyboardControls>;
}
